// AI provider settings. "Bundle of Babies" (BabyAI MoE) is the default
// and works with no setup; Anthropic (Claude) is the BYOK alternative.
// Both are browser-direct — keys/tokens stay in the browser.

import { useState } from "react";
import { useKeyStore, type Provider } from "../store/keyStore";
import { MODELS as ANTHROPIC_MODELS } from "../lib/anthropic-client";
import { BABYAI_MODELS } from "../lib/babyai-client";

export function ApiKeyModal({ onClose }: { onClose: () => void }) {
  const provider = useKeyStore((s) => s.provider);
  const anthropic = useKeyStore((s) => s.anthropic);
  const babyai = useKeyStore((s) => s.babyai);
  const anthropicModel = useKeyStore((s) => s.anthropicModel);
  const babyaiModel = useKeyStore((s) => s.babyaiModel);
  const setProvider = useKeyStore((s) => s.setProvider);
  const setAnthropicKey = useKeyStore((s) => s.setAnthropicKey);
  const clearAnthropicKey = useKeyStore((s) => s.clearAnthropicKey);
  const setBabyaiKey = useKeyStore((s) => s.setBabyaiKey);
  const setModel = useKeyStore((s) => s.setModel);

  const [anthropicInput, setAnthropicInput] = useState(anthropic ?? "");
  const [babyaiInput, setBabyaiInput] = useState(babyai ?? "");

  const save = () => {
    if (provider === "anthropic" && anthropicInput.trim()) setAnthropicKey(anthropicInput.trim());
    if (provider === "babyai" && babyaiInput.trim()) setBabyaiKey(babyaiInput.trim());
    onClose();
  };

  const Tab = ({ id, label }: { id: Provider; label: string }) => (
    <button
      className={"akm-tab" + (provider === id ? " akm-tab-on" : "")}
      onClick={() => setProvider(id)}
    >{label}</button>
  );

  return (
    <div className="akm-backdrop" onClick={onClose}>
      <div className="akm-card" onClick={(e) => e.stopPropagation()}>
        <h2 className="akm-title">Your writing model</h2>

        <div className="akm-tabs">
          <Tab id="babyai" label="Bundle of Babies" />
          <Tab id="anthropic" label="Anthropic (Claude)" />
        </div>

        {provider === "babyai" ? (
          <>
            <p className="akm-why">
              A little council of small open models — Christopher’s own MoE. It’s the default and
              needs no setup. Bring your own HuggingFace token only if you have your own access.
            </p>
            <label className="akm-label" htmlFor="akm-model">Model</label>
            <select id="akm-model" className="akm-input" value={babyaiModel} onChange={(e) => setModel("babyai", e.target.value)}>
              {BABYAI_MODELS.map((m) => <option key={m.id} value={m.id}>{m.label} — {m.blurb}</option>)}
            </select>
            <label className="akm-label" htmlFor="akm-hf">HuggingFace token <span className="akm-opt">(optional)</span></label>
            <input id="akm-hf" className="akm-input" type="password" autoComplete="off" placeholder="hf_… (leave blank to use the default)"
              value={babyaiInput} onChange={(e) => setBabyaiInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); }} />
          </>
        ) : (
          <>
            <p className="akm-why">
              Claude, billed to your own Anthropic account. The key lives in your browser only — it
              never reaches us. Get one at{" "}
              <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer">console.anthropic.com</a>.
            </p>
            <label className="akm-label" htmlFor="akm-model">Model</label>
            <select id="akm-model" className="akm-input" value={anthropicModel} onChange={(e) => setModel("anthropic", e.target.value)}>
              {ANTHROPIC_MODELS.map((m) => <option key={m.id} value={m.id}>{m.label} — {m.blurb}</option>)}
            </select>
            <label className="akm-label" htmlFor="akm-key">API key</label>
            <input id="akm-key" className="akm-input" type="password" autoComplete="off" placeholder="sk-ant-…"
              value={anthropicInput} onChange={(e) => setAnthropicInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); }} />
          </>
        )}

        <div className="akm-actions">
          <button className="akm-btn" onClick={save}>Save</button>
          {provider === "anthropic" && anthropic && <button className="akm-link" onClick={() => { clearAnthropicKey(); setAnthropicInput(""); }}>remove key</button>}
          <button className="akm-link" onClick={onClose}>close</button>
        </div>
      </div>

      <style>{`
        .akm-backdrop { position: fixed; inset: 0; z-index: 3000; background: rgba(40,34,22,0.32); display: flex; align-items: center; justify-content: center; }
        .akm-card { width: min(480px, 92vw); background: var(--paper-raised); border: 1px solid var(--line); border-radius: var(--radius-2); box-shadow: var(--shadow-2); padding: var(--space-6); }
        .akm-title { font-family: var(--font-serif); font-size: 22px; margin: 0 0 var(--space-4); color: var(--ink); }
        .akm-tabs { display: flex; gap: var(--space-1); background: var(--paper-sunk); border-radius: var(--radius-1); padding: 3px; margin-bottom: var(--space-4); }
        .akm-tab { flex: 1; font-family: var(--font-sans); font-size: 13px; font-weight: 600; color: var(--ink-soft); background: transparent; border: none; border-radius: 6px; padding: 8px; cursor: pointer; }
        .akm-tab-on { background: var(--paper-raised); color: var(--teal); box-shadow: var(--shadow-1); }
        .akm-why { font-size: 13px; line-height: 1.6; color: var(--ink-soft); margin: 0 0 var(--space-3); }
        .akm-label { display: block; font-size: 12px; font-weight: 600; color: var(--ink-soft); margin: var(--space-3) 0 var(--space-2); }
        .akm-opt { color: var(--ink-faint); font-weight: 400; }
        .akm-input { width: 100%; box-sizing: border-box; padding: 10px 12px; font-family: var(--font-mono); font-size: 13px; background: var(--paper); border: 1px solid var(--line-strong); border-radius: var(--radius-1); color: var(--ink); }
        .akm-input:focus { outline: none; border-color: var(--teal); }
        .akm-actions { display: flex; align-items: center; gap: var(--space-3); margin-top: var(--space-5); }
        .akm-btn { font-family: var(--font-sans); font-size: 14px; font-weight: 600; padding: 9px 20px; border-radius: var(--radius-1); border: 1px solid var(--teal); background: var(--teal); color: #fff; cursor: pointer; }
        .akm-btn:hover { background: var(--teal-bright); }
        .akm-link { background: none; border: none; color: var(--ink-faint); font-size: 13px; cursor: pointer; text-decoration: underline; }
        .akm-link:hover { color: var(--rust); }
      `}</style>
    </div>
  );
}
